import type { TranslationInput } from './types'

/**
 * Default copy for the mock OTP widget. The real SDK exposes a large `flow.*`
 * tree; we keep the few keys the app sets (`flow.cta`, `flow.close`, …) plus a
 * small `otp.*` namespace this mock owns. `setTranslations` deep-merges the
 * app's overrides on top, so app-provided strings win.
 */
const defaultTranslations = {
  en: {
    flow: {
      cta: 'Continue',
      close: 'Close',
    },
    otp: {
      welcome: 'Welcome back',
      title: 'Check your phone for a code',
      description: "Enter the code to confirm it's you",
      resend: 'Resend code',
      // Inline error copy, keyed by the BE's error.code (used by both the drawer
      // `start` flow and the modal `login` flow). Unknown codes fall back to the
      // server-provided message, then to `default`.
      errors: {
        INVALID_OTP: 'That code is incorrect. Please try again.',
        OTP_EXPIRED: 'That code has expired. Request a new one.',
        TOO_MANY_ATTEMPTS: 'Too many attempts. Please wait and try again.',
        SERVER_ERROR: 'Something went wrong. Please try again.',
        NETWORK: 'Network error. Check your connection and try again.',
        BAD_RESPONSE: 'Something went wrong. Please try again.',
        default: 'That code didn’t work. Please try again.',
      },
    },
    // Boost login-button flow (web-payments). {loginId} = the phone number.
    boost: {
      cta: 'Sign in with your\ndevice unlock method',
      header: 'Use One-time Code',
      description: 'We have sent you a 4-digit code to\n{loginId}',
      title: 'Enter the code',
      resendQuestion: "Didn't receive the SMS?",
      resend: 'Resend',
    },
  },
}

type Tree = Record<string, unknown>

let current: Tree = structuredCloneSafe(defaultTranslations)

function structuredCloneSafe<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

function isObject(value: unknown): value is Tree {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function deepMerge(target: Tree, source: Tree): Tree {
  for (const key of Object.keys(source)) {
    const next = source[key]
    if (isObject(next) && isObject(target[key])) {
      target[key] = deepMerge(target[key] as Tree, next)
    } else {
      target[key] = next
    }
  }
  return target
}

/** Merge caller-supplied translations over the current tree (app wins). */
export function setTranslations(input: TranslationInput): void {
  if (!isObject(input)) return
  current = deepMerge(current, structuredCloneSafe(input) as Tree)
}

/**
 * Resolve a dotted key (e.g. `otp.title`) for the active locale, falling back
 * to `en` then to the raw key. Supports `{loginId}`-style interpolation.
 */
export function t(
  key: string,
  vars: Record<string, string> = {},
  locale = 'en'
): string {
  const fromLocale = lookup(current[locale], key)
  const value = fromLocale ?? lookup(current.en, key) ?? key
  return interpolate(value, vars)
}

function lookup(scope: unknown, dottedKey: string): string | undefined {
  let node: unknown = scope
  for (const part of dottedKey.split('.')) {
    if (!isObject(node)) return undefined
    node = node[part]
  }
  return typeof node === 'string' ? node : undefined
}

function interpolate(template: string, vars: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (_, name: string) =>
    name in vars ? vars[name] : `{${name}}`
  )
}
